import React from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "rgba(255,255,255,0.03)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(0,225,255,0.55)" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.58)" },
  "& .MuiInputBase-input": { color: "#fff" },
};

export default function BannerPicker({ state, actions, error }) {
  const b = state.identidade.banner;

  const setMode = (mode) => {
    actions.set("identidade.banner.mode", mode);
  };

  const setUrl = (url) => {
    actions.set("identidade.banner.url", url);
    actions.set("identidade.banner.previewUrl", url);
  };

  const onPickFile = (file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    actions.set("identidade.banner.file", file);
    actions.set("identidade.banner.previewUrl", previewUrl);
  };

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      {error ? (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <ToggleButtonGroup
        exclusive
        value={b.mode}
        onChange={(_, v) => v && setMode(v)}
        sx={{
          gap: 1,
          "& .MuiToggleButton-root": {
            flex: 1,
            borderRadius: "14px !important",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.72)",
            fontWeight: 900,
            textTransform: "none",
            minHeight: 46,
            bgcolor: "rgba(255,255,255,0.03)",
          },
          "& .Mui-selected": {
            bgcolor: "rgba(0,225,255,0.1) !important",
            color: "#8ef4ff !important",
            borderColor: "rgba(0,225,255,0.28) !important",
          },
        }}
      >
        <ToggleButton value="url">
          <LinkRoundedIcon sx={{ mr: 1, fontSize: 18 }} /> URL
        </ToggleButton>
        <ToggleButton value="file">
          <UploadRoundedIcon sx={{ mr: 1, fontSize: 18 }} /> Upload
        </ToggleButton>
      </ToggleButtonGroup>

      {b.mode === "url" ? (
        <TextField
          fullWidth
          label="URL do banner"
          value={b.url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://images.unsplash.com/..."
          InputLabelProps={{ shrink: true }}
          sx={inputSx}
        />
      ) : (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          alignItems={{ md: "stretch" }}
        >
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadRoundedIcon />}
            sx={{
              minHeight: 54,
              px: 2,
              borderRadius: 2,
              fontWeight: 900,
              textTransform: "none",
              borderColor: "rgba(255,255,255,0.14)",
            }}
          >
            Selecionar imagem
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
          </Button>

          <TextField
            fullWidth
            label="Arquivo selecionado"
            value={b.file?.name || ""}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
          />
        </Stack>
      )}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: 260,
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,0.12)",
          background: b.previewUrl
            ? `linear-gradient(180deg, rgba(5,12,24,0.12), rgba(5,12,24,0.48)), url(${b.previewUrl}) center/cover no-repeat`
            : "linear-gradient(135deg, rgba(0,225,255,0.12), rgba(109,255,178,0.08))",
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        {!b.previewUrl ? (
          <Stack
            spacing={1.2}
            alignItems="center"
            justifyContent="center"
            sx={{ width: "100%", height: "100%", color: "rgba(255,255,255,0.55)" }}
          >
            <ImageRoundedIcon sx={{ fontSize: 40 }} />
            <Typography sx={{ fontWeight: 800 }}>Preview do banner</Typography>
            <Typography sx={{ fontSize: 13, textAlign: "center", maxWidth: 280 }}>
              A imagem escolhida vai aparecer aqui e também no preview lateral da página.
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ p: 1.5, width: "100%", background: "linear-gradient(180deg, transparent, rgba(4,8,18,0.88))" }}>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
              Como pode aparecer na listagem
            </Typography>
            <Typography sx={{ fontWeight: 900 }}>Banner carregado com sucesso</Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
}
